import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { OrganizationCategory } from '@safetyhub/shared';
import { Card } from '@safetyhub/ui';
import { adminCompaniesApi, extractApiError, toAbsoluteApiFileUrl } from '../lib/api';
import {
  DEFAULT_PHOTO_PURPOSE,
  IMPROVEMENT_IMAGES_TAB,
  IMPROVEMENT_IMAGES_TAB_LABEL,
  KNOWLEDGE_EXPORT_COLORS,
  KNOWLEDGE_EXPORT_TOOLTIP,
  NO_ANALYSIS_SUMMARY_LABEL,
  PHOTO_REVIEWED_LABEL,
  deriveFindingLabel,
  deriveGradeKeys,
  deriveKnowledgeExportBadge,
  derivePurposeTabs,
  mlPhotoGradeLabel,
  mlSourcesApi,
  purposeTabLabel,
  sumCounts,
  SOURCE_REPORT_DELETED_LABEL,
  type AdminImprovementImage,
  type AdminMlPhotoRow,
  type MlPhotoGrade,
} from '../lib/ml-sources';
import { PhotoGradeBadge } from '../components/PhotoGradeBadge';
import { MlPhotoThumbnail } from '../components/MlPhotoThumbnail';
import { MlPhotoContextPanel } from '../components/MlPhotoContextPanel';
import { ImageLightbox } from '../components/ImageLightbox';

/**
 * 머신러닝 소스 관리 — **메인 메뉴 하나 + 소스별 탭**(2026-10-07 사용자 결정).
 *
 * 「점검 사진과 모범사례 사진은 별도 메인 메뉴로 두기보다 머신러닝 소스 관리 메뉴의 서브메뉴나
 * 탭으로 구분해서 볼 수 있게 하면 되지 않을까? 나중에 다른 소스가 추가되면 서브메뉴나 탭을 늘리면
 * 되잖아?」 ⇒ 메뉴를 늘리지 않는다. 사진 소스 탭은 **서버 `purposeCounts`의 키에서 파생**하므로
 * 서버에 purpose가 추가되면 이 파일을 고치지 않아도 탭이 늘어난다(라벨만 `PHOTO_PURPOSE_LABELS`에
 * 추가하면 되고, 안 추가해도 raw 키로 탭은 뜬다).
 *
 * 탭 상태는 URL 쿼리(`?tab=`)에 둔다 — 새로고침·공유에 살아남아야 하므로.
 * 탭 패턴은 `pages/verification/Verification.tsx`를 따랐다(admin에서 `?tab=`을 쓰는 기존 화면).
 *
 * ⚠️ **조회 전용이다.** P28(사진 수동 삭제)은 계약이 명시적으로 범위 밖에 둔 미래 기능이다.
 */

/** 날짜 표시 시간대 — 슈퍼관리자 화면은 현장/조직 시간대를 알 수 없어 운영 기준(KST) 고정. */
const ADMIN_DATE_TZ = { timeZone: 'Asia/Seoul' } as const;

/** 건수 표시 포맷터 — 옵션 없는 toLocaleString() 호출을 피한다(ESLint). */
const COUNT_FORMAT = new Intl.NumberFormat('ko-KR');

const LIMIT = 50;

/** 조직 select 상한 — 서버 `/admin/companies`의 limit 천장. 잘리면 안내 문구로 드러낸다. */
const COMPANY_SELECT_LIMIT = 100;

function companyOptionLabel(o: {
  id: string;
  name: string;
  category: OrganizationCategory;
  ownerName: string | null;
}): string {
  const name = o.name || `(이름 없음) ${o.id.slice(0, 8)}`;
  const badge =
    o.category === 'WORKSPACE'
      ? ` (개인${o.ownerName ? `·${o.ownerName}` : ''})`
      : o.category === 'GUIDANCE_AGENCY' || o.category === 'SAFETY_MANAGEMENT_AGENCY'
        ? ' (기관)'
        : '';
  return `${name}${badge}`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    ...ADMIN_DATE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function MlSources() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? DEFAULT_PHOTO_PURPOSE;
  const isImprovementTab = activeTab === IMPROVEMENT_IMAGES_TAB;

  const [page, setPage] = useState(1);
  const [gradeFilter, setGradeFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  // 조직 select — 보고서 화면과 같은 소스(회사 목록). 구독 목록을 쓰면 개인 작업공간과
  // 구독 없는 회사가 빠져 필터로 도달할 수 없는 조직이 생긴다.
  const { data: orgData } = useQuery({
    queryKey: ['admin', 'ml-sources', 'companies'],
    queryFn: () =>
      adminCompaniesApi.list({ limit: COMPANY_SELECT_LIMIT, sortBy: 'name', sortOrder: 'asc' }),
  });
  const organizations = orgData?.data?.items ?? [];
  const organizationTotal = orgData?.data?.total ?? 0;
  const organizationsTruncated = organizationTotal > organizations.length;

  const photosQuery = useQuery({
    queryKey: ['admin', 'ml-photos', activeTab, page, gradeFilter, companyFilter],
    queryFn: () =>
      mlSourcesApi.listPhotos({
        page,
        limit: LIMIT,
        purpose: activeTab,
        grade: (gradeFilter || undefined) as MlPhotoGrade | undefined,
        companyId: companyFilter || undefined,
        // includeDeleted는 넘기지 않는다 — 서버 기본이 true(전수)이고 그것이 ML 화면의 계약이다.
      }),
    enabled: !isImprovementTab,
    placeholderData: (prev) => prev,
  });

  const improvementQuery = useQuery({
    queryKey: ['admin', 'ml-improvement-images', page, companyFilter],
    queryFn: () =>
      mlSourcesApi.listImprovementImages({
        page,
        limit: LIMIT,
        companyId: companyFilter || undefined,
      }),
    enabled: isImprovementTab,
    placeholderData: (prev) => prev,
  });

  // 탭 건수·전체 분포는 **사진 응답**이 소유한다(필터와 무관한 전수 — 계약 §2-3).
  // 개선 이미지 탭에 있는 동안에는 직전 사진 응답이 없을 수 있어 탭 건수는 비워 둔다
  // (탭 자체는 `derivePurposeTabs`가 라벨 맵 폴백으로 그리므로 사라지지 않는다).
  // ⚠️ `Record<string, number>`로 **넓혀서** 받는다 — 탭 키가 `string`이기 때문이다(서버가
  // 라벨 맵에 없는 purpose를 보내도 탭을 그려야 하므로 union으로 좁히면 인덱싱이 막힌다).
  const purposeCounts: Record<string, number> | undefined = photosQuery.data?.purposeCounts;
  /**
   * ⚠️ 등급 분포도 **같은 이유로 `Record<string, number>`로 넓혀서** 받는다 — 계약 타입은
   * `Record<MlPhotoGrade, number>`(아는 5종)인데 **런타임은 서버가 먼저 늘어난 코드를 담을 수
   * 있고**, 그때 그 키로 인덱싱해야 한다(union으로 좁히면 막힌다). 좁히지 말 것: 좁히면
   * `deriveGradeKeys`가 찾아낸 미지 키를 그릴 수 없어 다시 조용히 사라진다.
   */
  const gradeCounts: Record<string, number> | undefined = photosQuery.data?.gradeCounts;

  const purposeTabs = useMemo(() => derivePurposeTabs(purposeCounts), [purposeCounts]);

  /**
   * 등급 칩·필터가 그릴 키 — **서버 `gradeCounts`의 키까지 포함**한다(`deriveGradeKeys` JSDoc).
   * 탭과 같은 규율이다: 프론트가 아는 목록만 그리면 서버가 먼저 늘어난 사실이 화면에서 사라진다.
   */
  const gradeKeys = useMemo(() => deriveGradeKeys(gradeCounts), [gradeCounts]);

  /** 사진 전체 = purpose 분포의 합. 개선 이미지는 사진이 아니라 이 합에 넣지 않는다. */
  const photoTotalByPurpose = sumCounts(purposeCounts);
  const photoTotalByGrade = sumCounts(gradeCounts);
  /**
   * 두 분포는 둘 다 **필터 무관 전수**라 반드시 같아야 한다. 어긋나면 서버가 purpose 또는 등급
   * 키 하나를 빠뜨린 것이고, 그러면 그 탭의 사진이 조용히 사라진다 — 숫자로 드러낸다.
   */
  const countsDisagree =
    purposeCounts !== undefined &&
    gradeCounts !== undefined &&
    photoTotalByPurpose !== photoTotalByGrade;

  /**
   * 카운트가 센 범위 — 서버 카운트가 `companyId` 스코프를 따르므로 조직 필터가 걸린 동안은
   * 「전체」가 아니다. 두 라벨을 **한 조건에서 파생**시켜 머리말과 등급 분포가 어긋나지 않게 한다.
   */
  const isCompanyScoped = Boolean(companyFilter);
  const scopeLabel = isCompanyScoped ? '이 조직' : '전체';
  const photoTotalLabel = isCompanyScoped ? '이 조직 사진' : '사진 전체';

  const handleTabChange = (tab: string) => {
    setPage(1);
    setGradeFilter('');
    if (tab === DEFAULT_PHOTO_PURPOSE) {
      // 기본 탭이므로 파라미터를 제거한다(Verification과 같은 관용).
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const error = isImprovementTab ? improvementQuery.error : photosQuery.error;

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">머신러닝 소스 관리</h1>
          <p className="text-sm text-gray-500 mt-1">
            학습에 쓰이는 원본 소스를 전수로 본다 — 지운 것도 보여주고 품질 등급으로 가른다.
          </p>
        </div>
        <div className="text-right">
          {/* ⚠️ 두 카운트는 `companyId`·`includeDeleted` **스코프를 따른다**(grade·purpose 필터만
              무관). 그래서 조직 필터가 걸린 동안 이것을 「전체」라 부르면 거짓이다 — 라벨을 바꾼다.

              ⚠️ **분포가 없으면 줄 자체를 그리지 않는다 — 「0장」은 거짓이다.** `photosQuery`가
              `enabled: !isImprovementTab`이라 개선 이미지 탭에서는 응답이 없고, 탭을 눌러 이동한
              경우엔 `placeholderData`가 직전 값을 남기지만 **`?tab=improvement-images`로 직접
              진입하거나 그 탭에서 새로고침하면** 그 폴백이 없다(탭 상태를 URL에 두는 것은
              설계다 — 이 파일 머리말). 그때 `sumCounts(undefined)`가 `0`을 돌려줘 머리말이
              **「사진 전체 0장」**이라 적고 있었다(2026-10-07 P4-h).
              게이트는 **탭 건수와 같은 것**(`purposeCounts !== undefined`)을 쓴다 — 같은 사실을
              두 자리가 다른 기준으로 판정하면 한쪽이 거짓말한다. 「0장」으로 되돌리지 말 것:
              「모른다」와 「없다」는 다른 문장이고, 여기서 줄을 그리지 않는 것은
              **아무 주장도 하지 않는 것**이다(`FINDING_COUNT_UNKNOWN_LABEL`이 같은 규율의 짝). */}
          {purposeCounts !== undefined && (
            <div className="text-sm text-gray-500">
              {photoTotalLabel} {COUNT_FORMAT.format(photoTotalByPurpose)}장
            </div>
          )}
          {countsDisagree && (
            <div className="mt-1 text-xs text-red-600">
              ⚠️ 등급 합계 {COUNT_FORMAT.format(photoTotalByGrade)}장과 다르다 — 용도 또는 등급 키가
              빠졌다
            </div>
          )}
        </div>
      </div>

      {/* 탭 — 사진 소스는 서버 `purposeCounts`의 **키**에서 파생하고 개선 이미지는 고정 하나다.
          ⚠️ **라벨 맵(`PHOTO_PURPOSE_LABELS`)에 없는 키도 걸러내지 않고 raw 키로 그린다.**
          이것은 버그가 아니라 요구다 — 걸러내면 서버가 프론트보다 먼저 늘어났을 때 그 소스가
          화면에서 조용히 사라져 **「그 용도의 사진이 없다」와 「프론트가 탭을 빠뜨렸다」가 같은
          모양**이 된다. 지우지 말 것(판정 규칙은 `derivePurposeTabs`가 소유하고 회귀 spec
          「라벨 맵에 없는 용도가 와도 탭을 버리지 않고 raw 키로 그린다」가 지킨다). */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6 flex-wrap">
          {purposeTabs.map((purpose) => (
            <button
              key={purpose}
              onClick={() => handleTabChange(purpose)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === purpose
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {purposeTabLabel(purpose)}
              {purposeCounts?.[purpose] !== undefined && (
                <span className="text-xs text-gray-400">
                  {COUNT_FORMAT.format(purposeCounts[purpose])}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={() => handleTabChange(IMPROVEMENT_IMAGES_TAB)}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              isImprovementTab
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {IMPROVEMENT_IMAGES_TAB_LABEL}
            {improvementQuery.data && (
              <span className="text-xs text-gray-400">
                {COUNT_FORMAT.format(improvementQuery.data.total)}
              </span>
            )}
          </button>
        </nav>
      </div>

      {error ? (
        <div className="p-4 text-red-600">오류: {extractApiError(error).message}</div>
      ) : (
        <>
          {/* 필터 */}
          <Card radius="lg" className="mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="ml-company-filter">
                조직
              </label>
              <select
                id="ml-company-filter"
                value={companyFilter}
                onChange={(e) => resetPageAnd(() => setCompanyFilter(e.target.value))}
                className="w-full text-sm border rounded px-2 py-1.5 bg-white"
              >
                <option value="">전체 조직</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {companyOptionLabel(o)}
                  </option>
                ))}
              </select>
              {organizationsTruncated && (
                <p className="mt-1 text-xs text-gray-400">
                  상위 {organizations.length}곳만 표시 (총 {COUNT_FORMAT.format(organizationTotal)}
                  곳)
                </p>
              )}
            </div>
            {/* 등급 필터는 사진 탭에만 — 개선 이미지에는 등급 축이 없다.
                ⚠️ **「라벨 품질」 필터(미처리만·요약 있는 것만)는 «의도적으로 없다»**(P4-f ⓑ,
                2026-10-07 사용자가 명시적으로 범위에서 제외). 이 자리에 없는 이유를 적어 두는 것은
                다음 사람이 「왜 필터가 없나」를 처음부터 다시 판단하지 않게 하려는 것이다.
                **되짚을 조건**: ⓐ의 「내보내기 처리」 배지가 들어간 뒤 운영자가 **「아직 안 내보낸 것만
                보고 싶다」**고 하면 그때 만든다. 그때 필요한 것은 이 select 하나가 아니라 **서버
                쪽 필터**다(집계가 `inspection_findings` 조인이라 화면에서 거를 수 없다 — 페이지
                단위로 걸러 버리면 `total`과 분포가 어긋난다). */}
            {!isImprovementTab && (
              <div>
                <label className="block text-xs text-gray-500 mb-1" htmlFor="ml-grade-filter">
                  품질 등급
                </label>
                <select
                  id="ml-grade-filter"
                  value={gradeFilter}
                  onChange={(e) => resetPageAnd(() => setGradeFilter(e.target.value))}
                  className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                >
                  <option value="">전체 등급</option>
                  {/* 키 목록은 `deriveGradeKeys`가 소유한다 — `ML_PHOTO_GRADES.map`으로 되돌리지
                      말 것. 서버가 먼저 늘어나면 **그 등급으로 거를 길이 아예 없어진다**(분포
                      칩에는 뜨는데 필터에 없으면 운영자가 「왜 못 거르나」를 묻는다). 라벨은
                      `mlPhotoGradeLabel`의 폴백이 미지 코드를 코드만으로 그린다. */}
                  {gradeKeys.map((g) => {
                    const label = mlPhotoGradeLabel(g);
                    return (
                      <option key={g} value={g}>
                        {label ? `${g} · ${label}` : g}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </Card>

          {isImprovementTab ? (
            <ImprovementImagesTab
              items={improvementQuery.data?.items ?? []}
              total={improvementQuery.data?.total ?? 0}
              isLoading={improvementQuery.isLoading}
              page={page}
              onPageChange={setPage}
            />
          ) : (
            <PhotosTab
              items={photosQuery.data?.items ?? []}
              total={photosQuery.data?.total ?? 0}
              gradeCounts={gradeCounts}
              gradeKeys={gradeKeys}
              scopeLabel={scopeLabel}
              isLoading={photosQuery.isLoading}
              page={page}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * 등급 분포 요약 — 「왜 이 사진이 학습에 안 들어가나」에 답하는 자리.
 *
 * ⚠️ **「필터 무관」은 등급·용도 필터에만 참이다** — 서버 카운트가 `companyId`·`includeDeleted`
 * 스코프는 따르므로 조직 필터가 걸리면 *그 조직의* 분포다. 라벨에 그 범위를 적어
 * 운영자가 숫자를 잘못 읽지 않게 한다(`scopeLabel`).
 */
function GradeSummary({
  gradeCounts,
  gradeKeys,
  scopeLabel,
}: {
  /** 계약보다 넓다 — 호출부 주석 참조(런타임이 미지 등급 코드를 담을 수 있다). */
  gradeCounts: Record<string, number>;
  /**
   * 그릴 키 — **여기서 다시 정하지 않는다.** 소유자는 `deriveGradeKeys`이고 필터 select가 같은
   * 값을 쓴다. `ML_PHOTO_GRADES.map`으로 되돌리면 서버가 먼저 늘어난 등급의 건수가 이 줄에서
   * 조용히 사라지고, 머리말의 합(`sumCounts`는 전 키를 센다)과 **어긋난 채 아무 경고도 없다**
   * (`countsDisagree`는 purpose 합과 등급 합을 대조하므로 이 축을 못 잡는다).
   */
  gradeKeys: string[];
  scopeLabel: string;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500">{scopeLabel} 등급 분포 (등급·용도 필터 무관)</span>
      {gradeKeys.map((g) => (
        <span key={g} className="inline-flex items-center gap-1.5">
          {/* 미지 코드는 `PhotoGradeBadge`가 코드를 그대로 그린다(그 JSDoc의 요구) — 여기서
              `ML_PHOTO_GRADE_LABELS`를 다시 읽지 않는 이유다. */}
          <PhotoGradeBadge grade={g as MlPhotoGrade} />
          <span className="text-sm text-gray-700">{COUNT_FORMAT.format(gradeCounts[g] ?? 0)}</span>
        </span>
      ))}
    </div>
  );
}

function Pagination({
  page,
  total,
  onPageChange,
}: {
  page: number;
  total: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  if (totalPages <= 1) return null;
  return (
    <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
      <button
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        이전
      </button>
      <span className="text-sm text-gray-600">
        {page} / {totalPages}
      </span>
      <button
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        다음
      </button>
    </div>
  );
}

function PhotosTab({
  items,
  total,
  gradeCounts,
  gradeKeys,
  scopeLabel,
  isLoading,
  page,
  onPageChange,
}: {
  /**
   * ⚠️ 구조적 타입을 손으로 나열하지 않고 **계약 타입을 그대로** 쓴다 — 나열하면 계약에 칸이
   * 늘어날 때 이 자리가 조용히 낡고(tsc가 「그 칸이 없다」고만 말한다) 맥락 패널로 넘길 때
   * 칸이 빠진 객체가 간다.
   */
  items: AdminMlPhotoRow[];
  total: number;
  /**
   * ⚠️ 이 칸만 계약 타입보다 **넓다**(`Record<MlPhotoGrade, number>` → `string`). 위 규율의
   * 예외가 아니라 그 규율이 요구하는 것이다 — 런타임이 계약에 없는 등급 코드를 담을 수 있고
   * (서버 선배포 창), 그 키를 그려야 「없다」와 「빠뜨렸다」가 갈린다. 호출부 주석이 근거다.
   */
  gradeCounts: Record<string, number> | undefined;
  /** 등급 칩이 그릴 키 — 필터 select와 **같은 파생**(`deriveGradeKeys`)을 받는다. */
  gradeKeys: string[];
  /** 분포가 어느 범위를 센 것인지 — 조직 필터가 걸리면 「전체」가 아니다. */
  scopeLabel: string;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
}) {
  // 두 상태가 **따로** 있다: 맥락 패널(ⓓ)과 원본 lightbox. 사진 클릭은 패널을 열고, 패널의
  // 「원본 크게 보기」가 lightbox를 띄운다 — 종전의 줌·좌우 이동이 그대로 남는다.
  //
  // ⚠️ 패널은 **인덱스가 아니라 사진 id**로 잡는다. 인덱스로 잡으면 열려 있는 동안 백그라운드
  // 재조회가 목록을 바꿀 때 **같은 자리의 다른 사진**이 조용히 그려진다(맥락 패널에서 그것은
  // 「이 사진의 위험 요약」을 거짓으로 만든다). id로 잡으면 그 사진이 목록에서 사라지면 패널도
  // 닫힌다 — 틀린 맥락을 보여주는 것보다 낫다. lightbox는 종전처럼 인덱스를 쓴다(좌우 이동이
  // 목록 순서에 기대는 기능이라 그쪽은 인덱스가 맞는 축이다).
  const [panelPhotoId, setPanelPhotoId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const urls = items.map((p) => toAbsoluteApiFileUrl(p.url));
  const panelPhoto = panelPhotoId ? items.find((p) => p.id === panelPhotoId) : undefined;

  return (
    <>
      {gradeCounts && (
        <GradeSummary
          gradeCounts={gradeCounts}
          gradeKeys={gradeKeys}
          scopeLabel={scopeLabel}
        />
      )}

      <Card padding="none" radius="lg" className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm text-gray-600">
          이 조건 {COUNT_FORMAT.format(total)}장
        </div>
        {isLoading ? (
          <div className="px-4 py-8 text-center text-gray-500">로딩 중...</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">해당 조건의 사진이 없습니다.</div>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4 p-4">
            {items.map((photo) => (
              <li key={photo.id} className="border rounded-card overflow-hidden bg-white">
                {/* 흐림·탈색·「삭제됨」 라벨 규칙은 MlPhotoThumbnail이 소유한다(점검 상세와 공유).
                    `region`은 그 컴포넌트에서 옵셔널이라 점검 상세는 무영향이다. */}
                <MlPhotoThumbnail
                  url={photo.url}
                  thumbnailUrl={photo.thumbnailUrl}
                  deletedAt={photo.deletedAt}
                  region={photo.analysisRegion}
                  label={`사진 맥락 보기 (${photo.siteName})`}
                  onClick={() => setPanelPhotoId(photo.id)}
                />
                <div className="p-2 space-y-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <PhotoGradeBadge grade={photo.grade} />
                    {/* 사람이 검수한 사진 — dev 33%인데 admin에는 노출이 0건이었다.
                        조건부 렌더다(존재가 곧 상태를 뜻하는 요소라 빈 배지를 그리지 않는다). */}
                    {photo.isReviewed && (
                      <span className="rounded bg-green-100 px-1 py-0.5 text-[11px] font-medium text-green-700">
                        {PHOTO_REVIEWED_LABEL}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-700 truncate" title={photo.companyName}>
                    {photo.companyName}
                  </div>
                  <div className="text-xs text-gray-500 truncate" title={photo.siteName}>
                    {photo.siteName}
                    {photo.inspectionDeletedAt && (
                      <span className="ml-1 text-red-500">(점검 삭제)</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(photo.createdAt).toLocaleDateString('ko-KR', ADMIN_DATE_TZ)}
                  </div>
                  <PhotoLabelQuality photo={photo} />
                </div>
              </li>
            ))}
          </ul>
        )}
        <Pagination page={page} total={total} onPageChange={onPageChange} />
      </Card>

      {panelPhoto && (
        <MlPhotoContextPanel
          photo={panelPhoto}
          onClose={() => setPanelPhotoId(null)}
          // 패널을 닫지 않고 lightbox를 띄운다 — 닫으면 확대를 보고 돌아올 자리가 사라진다
          // (lightbox가 `z-[10000]`, Modal이 `z-[70]`이라 위로 얹힌다).
          onOpenOriginal={() =>
            setLightboxIndex(items.findIndex((p) => p.id === panelPhoto.id))
          }
          // 그 lightbox가 떠 있는 동안 패널의 Esc를 끈다 — Esc 한 번에 lightbox만 닫힌다.
          lightboxOpen={lightboxIndex !== null}
        />
      )}

      {lightboxIndex !== null && urls.length > 0 && (
        <ImageLightbox
          urls={urls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

/**
 * 타일의 **라벨 품질** 칸 셋(P4-f ⓐ) — 등급이 **문서화·삭제 상태**를 말하고, 이 칸들이
 * **분석 내용이 얼마나 채워졌나**를 말한다. 둘 다 **성질**이다(판정이 아니다).
 *
 * ⚠️ **요약·발견사항은 비어도 칸을 비우지 않는다** — 「분석 없음」·「발견사항 없음」이라고 **적는다.**
 * 「없다」는 것 자체가 **읽을 값**이고, 빈 칸으로 두면 **「없음」과 「못 읽었음」이 같은 모양**이 된다
 * (dev 실측으로 요약 없는 사진 7장 · 발견사항 없는 사진 9장이 실재한다).
 *
 * ⚠️ 이 칸들을 **「학습 제외」·「학습에 사용됨」류로 쓰지 말 것** — 등급과 같은 자다. 이 화면은
 * **무엇이 어떤 성질인지**를 보여주고 **모집단을 고르는 것은 학습 단계의 일**이다(근거·결정
 * 출처는 `lib/ml-sources.ts`의 `ML_PHOTO_GRADE_COLORS` JSDoc).
 *
 * ⚠️ 반대로 **「내보내기 처리」 배지는 조건부**다 — 존재가 곧 상태를 뜻하는 요소라 빈 배지를
 * 그리면 그 손실이 spec에서 초록으로 지나간다. 그리고 그 배지는 **세 상태**를 말한다 —
 * 「일부」를 뭉개면 운영자가 **남은 것을 못 찾는다**(판정은 `deriveKnowledgeExportBadge`가
 * 소유하고 이 컴포넌트는 그 결과만 그린다. ⚠️ 종전 이 자리에 적혀 있던 「전부 55·일부 7」은
 * **전수를 분모로 쓴 값**이고 그 7장이 거짓 「일부」였다 — 올바른 분모로는 **일부 0건**이다.
 * 실측과 분모 규약은 그 함수 JSDoc이 정본이다).
 *
 * ⚠️ 「사람 수정 N」을 **「교정」·「정정」으로 바꾸지 말 것** — `aiEdited`는 「AI가 틀려서 사람이
 * 바로잡았다」가 아니라 **「사람이 손댔다」**다(dev 96건 중 교정 쌍은 **2건**뿐이고, 9건은 애초에
 * AI가 만든 것도 아니다). 같은 이유로 배지 라벨에서 **「처리」를 빼지 말 것** — 두 축 모두
 * **이름이 실제보다 좁은 뜻을 암시하는** 칼럼이고 근거는 `lib/ml-sources.ts`의 그 두 JSDoc에 있다.
 */
function PhotoLabelQuality({ photo }: { photo: AdminMlPhotoRow }) {
  const exportBadge = deriveKnowledgeExportBadge(photo);
  const summary = photo.analysisSummary;

  return (
    <div className="mt-1.5 space-y-1 border-t border-gray-100 pt-1.5">
      <div
        className={`truncate text-xs ${summary ? 'text-gray-600' : 'text-gray-400'}`}
        title={summary ?? NO_ANALYSIS_SUMMARY_LABEL}
      >
        {summary ?? NO_ANALYSIS_SUMMARY_LABEL}
      </div>
      <div className="text-xs text-gray-500">{deriveFindingLabel(photo)}</div>
      {exportBadge && (
        <span
          // `title`이 그 칼럼의 뜻을 정확히 적는다 — 「등록됐다」·「학습에 사용됐다」가 아니라
          // 「내보내기 처리를 거쳤다」다(신뢰도 0.7 미달 미등록도 true. 상수 JSDoc에 dev 실측).
          title={KNOWLEDGE_EXPORT_TOOLTIP}
          className={`inline-block rounded px-1 py-0.5 text-[11px] font-medium ${
            KNOWLEDGE_EXPORT_COLORS[exportBadge.state]
          }`}
        >
          {exportBadge.label}
        </span>
      )}
    </div>
  );
}

function ImprovementImagesTab({
  items,
  total,
  isLoading,
  page,
  onPageChange,
}: {
  /**
   * ⚠️ 구조적 타입을 손으로 나열하지 않고 **계약 타입을 그대로** 쓴다 — 위 `PhotosTab`이 같은
   * 규율을 선언하고 있는데 이 형제만 손나열이었다(계약 13칸 중 **9칸**만 적혀 `mimeType`·
   * `sourceInspectionId`·`sourcePhotoId`·`companyId` 넷이 빠져 있었다). 나열하면 계약에 칸이
   * 늘 때 이 자리가 조용히 낡고(tsc가 「그 칸이 없다」고만 말한다) 맥락 패널로 넘길 때 칸이
   * 빠진 객체가 간다.
   */
  items: AdminImprovementImage[];
  total: number;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const urls = items.map((i) => toAbsoluteApiFileUrl(i.imageUrl));

  return (
    <>
      <Card padding="none" radius="lg" className="overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 text-sm text-gray-600">
          이 조건 {COUNT_FORMAT.format(total)}건
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이미지
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                요약
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                조직
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                출처 보고서
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                모델 · 사용
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                생성일시
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  개선 이미지가 없습니다.
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(index)}
                      aria-label="개선 이미지 열기"
                      className="block w-16 h-16 bg-gray-100 rounded overflow-hidden"
                    >
                      <img
                        src={toAbsoluteApiFileUrl(item.imageUrl)}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md">
                    <div className="line-clamp-3">{item.summaryText}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.companyName}</td>
                  <td className="px-4 py-3 text-sm">
                    {/* P11 계약의 핵심 — 이미지와 요약은 남고 출처 보고서는 비어 있을 수 있다 */}
                    {item.sourceReportId ? (
                      <span className="font-mono text-xs text-gray-500">
                        {item.sourceReportId.slice(0, 8)}
                      </span>
                    ) : (
                      <span className="text-xs text-red-600">{SOURCE_REPORT_DELETED_LABEL}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    <div>{item.aiModel}</div>
                    <div>
                      {COUNT_FORMAT.format(item.usageCount)}회
                      {!item.hasEmbedding && (
                        <span className="ml-1 text-amber-600">· 임베딩 없음</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {formatDateTime(item.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination page={page} total={total} onPageChange={onPageChange} />
      </Card>

      {lightboxIndex !== null && urls.length > 0 && (
        <ImageLightbox
          urls={urls}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
